import { FirebaseDatabase } from '@/context/FirebaseProvider'
import { FIREBASE_PREFIX } from '@/constants/constants'
import { onValue, query, ref } from 'firebase/database'
import React, { useContext, useEffect } from 'react'
import { useLocalObservable } from 'mobx-react'

export type Bet = {
  amount: string
  address: string
  block: number
  tx_hash: string
  date: string
  id: string
}

export type Player = {
  amount: string
  address: string
  chance: string
}

export enum GameStage {
  'INIT' = 'init',
  'PROCESSING' = 'processing',
  'DRAWING' = 'drawing',
  'FINISHED' = 'finished',
}

export type GameState = {
  id: string
  bets: Array<Bet>
  players: Array<Player>
  pool: string
  start_block: number
  draw_timestamp: number
  state: GameStage
  player_winner: string | null
  win_amount: string
  randomness: string
  isRoundFinished: boolean
  finishedTimestamp: number
  drawRound: null | number
  drawRoundTimestamp: null | number
}

const initialState = {
  id: '0',
  bets: [],
  players: [],
  pool: '0',
  start_block: 0,
  state: GameStage.INIT,
  draw_timestamp: 0,
  player_winner: null,
  win_amount: '0',
  randomness: '',
  isRoundFinished: false,
  finishedTimestamp: 0,
  drawRound: null,
  drawRoundTimestamp: null,
}

export const JackpotProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const store = useLocalObservable<{
    state: GameState
    setState: (data: GameState) => void
    setRoundFinished: () => void
    setRoundStarted: () => void
  }>(() => ({
    state: initialState,
    setState(data) {
      store.state = data
    },
    setRoundFinished() {
      store.state = {
        ...store.state,
        isRoundFinished: true,
      }
    },
    setRoundStarted() {
      store.state = {
        ...store.state,
        isRoundFinished: false,
      }
    },
  }))

  useEffect(() => {
    const sub = onValue(query(ref(FirebaseDatabase, `${FIREBASE_PREFIX}/jackpot`)), (snapshot) => {
      store.setState({ isRoundFinished: store.state.isRoundFinished, ...transformGameState(snapshot.val()) })
    })

    return () => {
      sub()
    }
  }, [])

  return <JackpotContext.Provider value={store}>{children}</JackpotContext.Provider>
}

const transformGameState = (data: any) => {
  return {
    id: data.id,
    bets: data.bets ? data.bets : [],
    players: data.players ? data.players : [],
    pool: data.pool,
    state: getJackpotState(data.state),
    start_block: 0,
    draw_timestamp: data.draw_timestamp,
    player_winner: data.player_winner,
    win_amount: data.win_amount,
    randomness: '',
    finishedTimestamp: data.finished_timestamp ? data.finished_timestamp : 0,
    drawRound: data.draw_round === 0 ? null : data.draw_round,
    drawRoundTimestamp: data.draw_timestamp === 0 ? null : parseInt(data.draw_timestamp?.slice(0, 13)),
  }
}

const getJackpotState = (state: any) => {
  switch (state) {
    case 'init':
      return GameStage.INIT
    case 'processing':
      return GameStage.PROCESSING
    case 'drawing':
      return GameStage.DRAWING
    case 'finished':
      return GameStage.FINISHED
    default:
      return GameStage.INIT
  }
}

export const JackpotContext = React.createContext<{
  state: GameState
  setRoundFinished: () => void
  setRoundStarted: () => void
}>({
  state: initialState,
  setRoundFinished: () => {},
  setRoundStarted: () => {},
})

export const useJackpot = () => useContext(JackpotContext)
